import { ref, computed } from 'vue';
import { authService, type AuthUser } from '../firebase/services/authService';
import { databaseService } from '../firebase/services/databaseService';
import type { User } from '../firebase/schema';

// Global auth state
const currentUser = ref<AuthUser | null>(null);
const userProfile = ref<User | null>(null);
const isLoading = ref(true);
const error = ref<string | null>(null);

// Auth state listener
let unsubscribeAuth: (() => void) | null = null;

export function useAuth() {
  // Computed properties
  const isAuthenticated = computed(() => !!currentUser.value);
  const isAnonymous = computed(() => currentUser.value?.isAnonymous ?? false);
  const displayName = computed(() => {
    return userProfile.value?.displayName || 
           currentUser.value?.displayName || 
           currentUser.value?.email || 
           'User';
  });

  // Methods
  const signInWithEmail = async (email: string, password: string) => {
    try {
      error.value = null;
      isLoading.value = true;
      const user = await authService.signInWithEmail(email, password);
      await loadUserProfile(user.uid);
      return user;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const createAccountWithEmail = async (email: string, password: string, displayName?: string) => {
    try {
      error.value = null;
      isLoading.value = true;
      const user = await authService.createAccountWithEmail(email, password, displayName);
      await loadUserProfile(user.uid);
      return user;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const signInWithGoogle = async () => {
    try {
      error.value = null;
      isLoading.value = true;
      const user = await authService.signInWithGoogle();
      await loadUserProfile(user.uid);
      return user;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const signInAnonymously = async () => {
    try {
      error.value = null;
      isLoading.value = true;
      const user = await authService.signInAnonymously();
      await loadUserProfile(user.uid);
      return user;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const signOut = async () => {
    try {
      error.value = null;
      await authService.signOut();
      currentUser.value = null;
      userProfile.value = null;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    }
  };

  const sendPasswordResetEmail = async (email: string) => {
    try {
      error.value = null;
      await authService.sendPasswordResetEmail(email);
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    }
  };

  const updateUserProfile = async (updates: { displayName?: string; photoURL?: string }) => {
    try {
      error.value = null;
      await authService.updateUserProfile(updates);
      
      // Update local user profile
      if (userProfile.value) {
        userProfile.value = {
          ...userProfile.value,
          ...updates
        };
      }
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    }
  };

  const loadUserProfile = async (userId: string) => {
    try {
      const profile = await databaseService.getUserProfile(userId);
      userProfile.value = profile;
    } catch (err) {
      console.error('Error loading user profile:', err);
      // Don't throw here as auth might still be successful
    }
  };

  const updateUserPreferences = async (preferences: Partial<User['preferences']>) => {
    if (!currentUser.value || !userProfile.value) {
      throw new Error('No authenticated user');
    }

    try {
      error.value = null;
      const updatedProfile = {
        ...userProfile.value,
        preferences: {
          ...userProfile.value.preferences,
          ...preferences
        }
      };

      await databaseService.createUserProfile(currentUser.value.uid, updatedProfile);
      userProfile.value = updatedProfile;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    }
  };

  const linkAnonymousAccount = async (email: string, password: string) => {
    try {
      error.value = null;
      isLoading.value = true;
      const user = await authService.linkWithEmail(email, password);
      await loadUserProfile(user.uid);
      return user;
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : 'An error occurred';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Initialize auth state listener
  const initializeAuth = () => {
    if (unsubscribeAuth) return; // Already initialized

    unsubscribeAuth = authService.onAuthStateChanged(async (user) => {
      currentUser.value = user;
      
      if (user) {
        await loadUserProfile(user.uid);
      } else {
        userProfile.value = null;
      }
      
      isLoading.value = false;
    });
  };

  // Cleanup auth listener
  const cleanupAuth = () => {
    if (unsubscribeAuth) {
      unsubscribeAuth();
      unsubscribeAuth = null;
    }
  };

  return {
    // State
    currentUser: computed(() => currentUser.value),
    userProfile: computed(() => userProfile.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    
    // Computed
    isAuthenticated,
    isAnonymous,
    displayName,
    
    // Methods
    signInWithEmail,
    createAccountWithEmail,
    signInWithGoogle,
    signInAnonymously,
    signOut,
    sendPasswordResetEmail,
    updateUserProfile,
    updateUserPreferences,
    linkAnonymousAccount,
    
    // Lifecycle
    initializeAuth,
    cleanupAuth,
    
    // Utilities
    clearError: () => { error.value = null; }
  };
}

// Global auth initialization for the app
export function initializeGlobalAuth() {
  const { initializeAuth } = useAuth();
  initializeAuth();
}

// Global auth cleanup
export function cleanupGlobalAuth() {
  const { cleanupAuth } = useAuth();
  cleanupAuth();
}